import { useMemo, useState } from "react";
import { uuidToEndpointId } from "../../lib/inventoryRows.js";
import { useDimensionEntry } from "../../lib/dimensionDefs.js";
import { hasPermission, liveEscalations, useMyPermissions } from "../../lib/permissions.js";
import { PERMISSIONS } from "../../lib/permissionKeys.js";
import { ACTION_TEXT_CLASS } from "../../lib/ui.js";
import {
    useEndpointNames,
    useEscalationCoveringEndpoint,
    useSubjectNames,
} from "../../lib/escalationTargets.js";
import {
    canEscalateOnSubject,
    canRequestEscalation,
    subjectKey,
    subjectLabel,
} from "../../lib/escalationClient.js";
import { Countdown } from "./EscalationParts.jsx";
import EscalationRequestDialog from "./EscalationRequestDialog.jsx";

export function deepestStep(scopePath) {
    const step = Array.isArray(scopePath) && scopePath.length ? scopePath[scopePath.length - 1] : null;
    if (!step || !step.id || (Array.isArray(step.ids) && step.ids.length > 1)) return null;
    return step;
}

/**
 * A scope step names a person by their provider id alone, and a target needs
 * the provider and integration too, so the dialog resolves the rest against the
 * directory and matches back on the id.
 *
 * `identityEntry` is the live label-cache entry. The directory search matches
 * display name, email and UPN but never the source id, so a seed taken from a
 * cold cache would search for a provider user id and find nobody: the
 * id-as-label fallback counts as having no name at all.
 */
export function escalatableTarget(scopePath, labelFor, identityEntry = null) {
    const step = deepestStep(scopePath);
    if (!step) return null;
    if (step.dim === "endpoints") {
        return {
            kind: "endpoint",
            endpoint: { id: uuidToEndpointId(step.id), label: labelFor?.(step) || step.id },
        };
    }
    if (step.dim === "identities") {
        const label = identityEntry?.label ?? "";
        const name = label && label !== step.id ? label : "";
        return {
            kind: "identity",
            person: {
                sourceId: step.id,
                search: identityEntry?.meta?.email || name,
                label: name,
            },
        };
    }
    return null;
}

export default function ScopeEscalationControl({ scopePath, labelFor }) {
    const caps = useMyPermissions();
    const [dialogOpen, setDialogOpen] = useState(false);
    const step = useMemo(() => deepestStep(scopePath), [scopePath]);
    // Subscribed, not snapshotted: the name and email arrive asynchronously, and
    // a seed frozen before they land searches for a provider id.
    const identityEntry = useDimensionEntry(
        "identities",
        step?.dim === "identities" ? step.id : null,
    );
    const target = useMemo(
        () => escalatableTarget(scopePath, labelFor, identityEntry),
        [scopePath, labelFor, identityEntry],
    );
    const seed = useMemo(
        () => (target?.kind === "endpoint"
            ? { endpoint: target.endpoint }
            : { person: target?.person }),
        [target],
    );

    // Only a window over THIS scope. One held over something else, announced
    // here with its own countdown, reads as if it applies to what you are
    // looking at. The account page lists every window held.
    const live = liveEscalations(caps);
    const named = live.find((escalation) => (target?.kind === "endpoint"
        ? escalation.endpointId === target.endpoint.id
        : target?.kind === "identity" && escalation.subject?.sourceId === target.person.sourceId));

    // A person-only window reaches its target on every machine they own, so
    // this machine is covered through them even though nothing names it.
    const personSubjects = useMemo(
        () => (named || target?.kind !== "endpoint"
            ? []
            : live
                .filter((escalation) => escalation.subject && !escalation.endpointId)
                .map((escalation) => escalation.subject)),
        [named, target, live],
    );
    const coveringPerson = useEscalationCoveringEndpoint(
        target?.kind === "endpoint" ? target.endpoint.id : "",
        personSubjects,
        hasPermission(caps, PERMISSIONS.DIRECTORY_DATA_READ),
    );
    const throughPerson = coveringPerson
        ? live.find((escalation) => escalation.subject
            && !escalation.endpointId
            && subjectKey(escalation.subject) === subjectKey(coveringPerson))
        : null;
    const shown = named ?? throughPerson;

    const subjectNames = useSubjectNames(
        shown?.subject ? [shown.subject] : [],
        hasPermission(caps, PERMISSIONS.DIRECTORY_DATA_READ),
    );
    const endpointNames = useEndpointNames(
        shown?.endpointId ? [shown.endpointId] : [],
        hasPermission(caps, PERMISSIONS.ENDPOINT_METADATA_READ),
    );

    const askable = target?.kind !== "identity" || canEscalateOnSubject(caps);

    if (!shown && !(target && askable && canRequestEscalation(caps))) return null;

    if (shown) {
        const machine = endpointNames.get(shown.endpointId);
        return (
            <span className="inline-flex items-center gap-[8px] border border-[color:var(--green)] bg-[color:var(--green-soft)] px-[10px] py-[5px]">
                <span className="text-[11px] leading-[1.1] font-medium text-[color:var(--ink)]">
                    {named ? "Temporary access here" : "One person's activity here"}
                </span>
                {shown.subject || machine?.hostname ? (
                    <span className="max-w-[160px] truncate text-[11px] leading-[1.1] text-[color:var(--muted)]">
                        {shown.subject
                            ? (subjectNames.get(subjectKey(shown.subject))
                                || subjectLabel(shown.subject))
                            : machine.hostname}
                    </span>
                ) : null}
                <Countdown expiresAt={shown.expiresAt} />
            </span>
        );
    }

    return (
        <>
            <button
                type="button"
                onClick={() => setDialogOpen(true)}
                className="inline-flex items-center border border-[color:var(--line)] bg-[color:var(--control-bg)] px-[10px] py-[6px] text-[color:var(--muted)] transition-colors hover:border-[color:var(--line-strong)] hover:text-[color:var(--ink)] cursor-pointer"
            >
                <span className={ACTION_TEXT_CLASS}>Request access</span>
            </button>
            <EscalationRequestDialog
                open={dialogOpen}
                onClose={() => setDialogOpen(false)}
                seed={seed}
            />
        </>
    );
}
